import React, { useState, useEffect, useCallback } from 'react';
import Layout from '../components/Layout';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Loader2, Bot, Activity, ShieldAlert } from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';
import { useWorkspace } from '../context/WorkspaceContext';
import { formatDateTime } from '../lib/utils';

export default function AIAgentsPage() {
    const { currentWorkspace } = useWorkspace();
    const [agents, setAgents] = useState([]);
    const [loading, setLoading] = useState(true);
    const [execsOpen, setExecsOpen] = useState(null);
    const [executions, setExecutions] = useState([]);

    const load = useCallback(async () => {
        if (!currentWorkspace?.id) return;
        try {
            setLoading(true);
            const res = await api.get('/agents', { params: { workspace_id: currentWorkspace.id } });
            setAgents(res.data || []);
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Gagal memuat agents');
        } finally {
            setLoading(false);
        }
    }, [currentWorkspace?.id]);

    useEffect(() => { load(); }, [load]);

    const showExecs = async (a) => {
        try {
            const res = await api.get(`/agents/${a.id}/executions`);
            setExecutions(res.data || []);
            setExecsOpen(a);
        } catch (e) { toast.error('Gagal memuat log eksekusi'); }
    };

    return (
        <Layout title="AI Agents" subtitle="Agent AI yang telah di-assign ke workspace ini — read-only">
            <div className="flex items-center gap-2 mb-4 p-3 rounded-lg bg-amber-50 border border-amber-200 text-amber-900 text-sm" data-testid="agent-readonly-notice">
                <ShieldAlert className="w-4 h-4 flex-shrink-0" />
                <span>AI Agent dikelola secara terpusat oleh Superadmin. Anda dapat meng-<strong>assign</strong> agent ke task seperti member biasa.</span>
            </div>

            {loading ? (
                <div className="flex items-center justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-brand-primary" /></div>
            ) : agents.length === 0 ? (
                <div className="text-center py-20 text-brand-text-muted" data-testid="agent-empty-state">
                    <Bot className="w-12 h-12 mx-auto mb-3 opacity-40" />
                    <p>Belum ada AI Agent yang di-assign ke workspace ini.</p>
                    <p className="text-xs mt-1">Hubungi Superadmin untuk menambahkan agent.</p>
                </div>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {agents.map((a) => (
                        <Card key={a.id} className="p-4 border-brand-border" data-testid={`agent-card-${a.id}`}>
                            <div className="flex items-start justify-between">
                                <div className="flex items-center gap-3">
                                    <div className="w-10 h-10 rounded-full bg-brand-accent-soft flex items-center justify-center">
                                        <Bot className="w-5 h-5 text-brand-primary" />
                                    </div>
                                    <div>
                                        <div className="font-semibold">{a.name}</div>
                                        <div className="text-xs text-brand-text-muted">{a.user?.email}</div>
                                    </div>
                                </div>
                                <Badge variant={a.status === 'blocked' ? 'destructive' : 'secondary'}>{a.status || 'idle'}</Badge>
                            </div>

                            <div className="mt-3 text-xs text-brand-text-muted">Model: <span className="font-mono">{a.llm_model}</span></div>
                            <div className="mt-1 flex flex-wrap gap-1">
                                {(a.tools_scope || []).map((t) => (
                                    <span key={t} className="text-[10px] px-1.5 py-0.5 bg-brand-accent-soft rounded">{t}</span>
                                ))}
                            </div>

                            <div className="mt-3 flex gap-1">
                                <Button variant="ghost" size="sm" onClick={() => showExecs(a)} data-testid={`agent-execs-${a.id}`}>
                                    <Activity className="w-4 h-4 mr-1" /> Log Eksekusi
                                </Button>
                            </div>
                        </Card>
                    ))}
                </div>
            )}

            <Dialog open={!!execsOpen} onOpenChange={(o) => !o && setExecsOpen(null)}>
                <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="agent-execs-dialog">
                    <DialogHeader>
                        <DialogTitle>Log Eksekusi — {execsOpen?.name}</DialogTitle>
                    </DialogHeader>
                    {executions.length === 0 ? (
                        <div className="text-center py-8 text-brand-text-muted">Belum ada eksekusi. Assign task ke agent untuk memicu.</div>
                    ) : (
                        <div className="space-y-3">
                            {executions.map((e) => (
                                <div key={e.id} className="p-3 border rounded-lg">
                                    <div className="flex items-center justify-between">
                                        <div className="text-sm font-medium">Task: <span className="font-mono">{e.task_id?.slice(0, 8)}...</span></div>
                                        <Badge variant={e.status === 'success' ? 'secondary' : (e.status === 'failed_max' || e.status === 'failed') ? 'destructive' : 'outline'}>
                                            {e.status} (attempt {e.attempt})
                                        </Badge>
                                    </div>
                                    <div className="text-xs text-brand-text-muted mt-1">{formatDateTime(e.created_at)}</div>
                                    {e.error && <div className="text-xs text-red-600 mt-1 font-mono">{e.error}</div>}
                                    <div className="mt-2 space-y-1">
                                        {(e.logs || []).map((log, i) => (
                                            <div key={i} className="text-xs flex gap-2">
                                                <span className="text-brand-text-muted">{log.ts?.slice(11, 19)}</span>
                                                <span>{log.message}</span>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </DialogContent>
            </Dialog>
        </Layout>
    );
}
